/**
 * One developer's day, as their agents reported it.
 *
 * The report a developer is otherwise asked to write at the end of every day — what they worked on,
 * for which bug or specification, in which repositories, how long, what changed — built from what
 * the agents already said. The developer reads their own and can copy it as the report; a manager
 * reads it instead of asking for it.
 *
 * One person at a time, on purpose. There is no view that puts two days side by side, and nothing
 * here is a score: the figures are what agents reported, next to what they were reported about.
 */

import { useMemo, useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Bug as BugIcon, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Compass, Download, FileText,
  GitCommitHorizontal, ListChecks, Lock, MessageSquare, ShieldAlert, Ticket,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatCompact, formatDuration, formatNumber, formatTime } from '../lib/format'
import { costLine, dayCsv, dayMarkdown, subjectOf } from '../lib/dayReport'
import { Badge, CopyButton, EmptyState, ErrorBanner, Panel, ProgressBar, Skeleton } from '../components/primitives'
import { Portrait, lookFor } from '../bughunt/Figures'
import type { DaySession, PromptItem, WorkCategory } from '../lib/types'

/** Today, as YYYY-MM-DD in the reader's own time zone. */
function today(): string {
  const now = new Date()
  return toIsoDate(now)
}

function toIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * The reader's local day as two instants.
 *
 * The API works in instants and knows nothing of time zones; the day boundary belongs to whoever is
 * reading. A manager in São Paulo asking for "Tuesday" means their Tuesday, not UTC's.
 */
function dayWindow(date: string): { from: string; to: string } {
  const [y, m, d] = date.split('-').map(Number)
  const start = new Date(y!, (m ?? 1) - 1, d ?? 1)
  const end = new Date(start)
  end.setDate(end.getDate() + 1)
  return { from: start.toISOString(), to: end.toISOString() }
}

function shift(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const value = new Date(y!, (m ?? 1) - 1, d ?? 1)
  value.setDate(value.getDate() + days)
  return toIsoDate(value)
}

/** /me/day: the signed-in developer's own day. */
export function MyDay() {
  const { data: me, isLoading } = useQuery({ queryKey: ['me'], queryFn: api.auth.me })
  const { t } = useI18n()

  if (isLoading) return <Skeleton height={120} />
  if (!me?.developerId) {
    return (
      <Panel>
        <EmptyState title={t('day.noDeveloper')} body={t('day.noDeveloperHint')} />
      </Panel>
    )
  }

  // In Acompanhar › Meu dia, inside the tab: the same day another person's link opens.
  return <DevDay developerId={me.developerId} />
}

export default function DevDay({ developerId }: { developerId?: string } = {}) {
  const { id: routeId = '' } = useParams()
  const id = developerId ?? routeId
  const { t, locale } = useI18n()
  const [params, setParams] = useSearchParams()
  const date = params.get('date') ?? today()
  const window_ = useMemo(() => dayWindow(date), [date])

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['developer-day', id, date],
    queryFn: () => api.developers.day(id, { ...window_, includeSimulated: true }),
    enabled: Boolean(id),
    // Today is still happening; any other day is history and does not need polling.
    refetchInterval: date === today() ? 20_000 : false,
  })

  const look = useMemo(() => (data ? lookFor(data.developerId, data.avatar) : null), [data])

  // Kept prompts, when the organization keeps them: fetched once for the day, shown per session.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const { data: prompts } = useQuery({
    queryKey: ['developer-prompts', id, date],
    queryFn: () => api.developers.prompts(id, window_),
    enabled: Boolean(id) && me?.promptTextCollected === true,
    refetchInterval: date === today() ? 30_000 : false,
  })
  // The developer's own security findings for the day — on their own day only.
  const { data: findings } = useQuery({
    queryKey: ['security', 'mine', date],
    queryFn: () => api.security.mine(window_),
    enabled: Boolean(me?.developerId) && me?.developerId === id,
    refetchInterval: date === today() ? 30_000 : false,
  })

  const promptsBySession = useMemo(() => {
    const map = new Map<string, PromptItem[]>()
    for (const prompt of prompts ?? []) {
      if (!prompt.workSessionId) continue
      map.set(prompt.workSessionId, [...(map.get(prompt.workSessionId) ?? []), prompt])
    }
    return map
  }, [prompts])

  // Capitalised at the first letter only: "segunda-feira, 21 de setembro", not "Segunda-Feira, 21 De".
  const formatted = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    .format(new Date(`${date}T12:00:00`))
  const dateLabel = formatted.charAt(0).toUpperCase() + formatted.slice(1)

  const setDate = (value: string): void => {
    const next = new URLSearchParams(params)
    if (value === today()) next.delete('date')
    else next.set('date', value)
    setParams(next, { replace: true })
  }

  const forbidden = error instanceof ApiError && error.status === 403

  const downloadCsv = (): void => {
    if (!data) return
    const blob = new Blob(['\uFEFF', dayCsv(data, date)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `devpulse-${data.displayName.replace(/\s+/g, '-').toLowerCase()}-${date}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="stack stack--loose">
      <div className="row row--wrap">
        {look && <Portrait look={look} size={48} />}
        <div className="stack" style={{ gap: 0 }}>
          <h1 className="day__name">{data?.displayName ?? t('day.title')}</h1>
          <span className="small muted">
            {data?.teamName ?? t('common.unassigned')} · {dateLabel}
          </span>
        </div>

        <div className="spacer" />

        <div className="row row--tight">
          <button type="button" className="btn btn--sm btn--ghost btn--icon" onClick={() => setDate(shift(date, -1))} aria-label={t('day.previous')}>
            <ChevronLeft size={15} />
          </button>
          <label className="row row--tight">
            <CalendarDays size={14} className="muted" />
            <input
              type="date"
              className="input"
              style={{ width: 150 }}
              value={date}
              max={today()}
              onChange={(event) => event.target.value && setDate(event.target.value)}
              aria-label={t('day.pickDate')}
            />
          </label>
          <button
            type="button"
            className="btn btn--sm btn--ghost btn--icon"
            onClick={() => setDate(shift(date, 1))}
            disabled={date >= today()}
            aria-label={t('day.next')}
          >
            <ChevronRight size={15} />
          </button>
          {date !== today() && (
            <button type="button" className="btn btn--sm" onClick={() => setDate(today())}>{t('day.today')}</button>
          )}
        </div>

        {data && data.sessions.length > 0 && (
          <div className="row row--tight">
            <CopyButton text={dayMarkdown(data, dateLabel, t, locale)} copyLabel={t('day.copyReport')} copiedLabel={t('day.copied')} />
            <button type="button" className="btn btn--sm btn--ghost" onClick={downloadCsv}>
              <Download size={13} /> CSV
            </button>
          </div>
        )}
      </div>

      {/*
        Said once, before the numbers. These are what agents reported about this person's work,
        and they describe work done with an agent — not a person's whole day.
      */}
      <p className="tiny muted">{t('day.note')}</p>

      {forbidden ? (
        <Panel>
          <EmptyState title={t('day.forbidden')} body={t('day.forbiddenHint')} />
        </Panel>
      ) : error ? (
        <ErrorBanner message={error instanceof Error ? error.message : String(error)} onRetry={() => void refetch()} retryLabel={t('common.retry')} />
      ) : isLoading || !data ? (
        <div className="stack">
          <Skeleton height={90} />
          <Skeleton height={160} />
        </div>
      ) : (
        <>
          <div className="day__totals">
            <Total label={t('day.time')} value={formatDuration(data.totals.activeSeconds, locale)} />
            <Total label={t('day.sessions')} value={String(data.totals.sessions)} />
            <Total
              label={t('day.lines')}
              value={
                <>
                  <span style={{ color: 'var(--ok-text)' }}>+{formatNumber(data.totals.linesAdded, locale)}</span>{' '}
                  <span className="day__minor" style={{ color: 'var(--danger-text)' }}>−{formatNumber(data.totals.linesDeleted, locale)}</span>
                </>
              }
            />
            <Total label={t('day.files')} value={String(data.totals.filesChanged)} />
            <Total label={t('day.commits')} value={String(data.totals.commits)} />
            <Total label={t('day.tests')} value={data.totals.testsRun > 0 ? `${data.totals.testsPassed}/${data.totals.testsRun}` : '—'} />
            <Total label={t('day.prompts')} value={String(data.totals.prompts)} />
            <Total label={t('day.tokens')} value={formatCompact(data.totals.inputTokens + data.totals.outputTokens, locale)} />
            <Total
              label={t('live.cost')}
              value={costLine(data, locale)}
              foot={data.totals.costAccuracy === 'Estimated' ? t('cost.Estimated') : undefined}
            />
          </div>

          <div className="row row--wrap tiny muted">
            <span>{t('day.repositories')}:</span>
            {data.totals.repositories.length === 0
              ? <span>—</span>
              : data.totals.repositories.map((repo) => <span key={repo} className="chip mono">{repo}</span>)}
            <div className="spacer" />
            {data.totals.bugs > 0 && <Badge tone="danger">{t('day.bugsCount', { count: data.totals.bugs })}</Badge>}
            {data.totals.specs > 0 && <Badge tone="info">{t('day.specsCount', { count: data.totals.specs })}</Badge>}
            {data.totals.explorations > 0 && <Badge>{t('day.explorationsCount', { count: data.totals.explorations })}</Badge>}
          </div>

          {data.sessions.length === 0 ? (
            <Panel>
              <EmptyState title={t('day.empty')} body={t('day.emptyHint')} />
            </Panel>
          ) : (
            <div className="stack">
              {findings && findings.length > 0 && (
                <div className="security-notice">
                  <ShieldAlert size={14} aria-hidden />
                  <div className="stack" style={{ gap: 4 }}>
                    <span className="small bold">{t('security.mineTitle', { count: findings.length })}</span>
                    <span className="tiny">{t('security.mineHint')}</span>
                    <span className="row row--wrap" style={{ gap: 6 }}>
                      {findings.map((finding) => (
                        <Badge key={finding.id} tone={finding.severity === 'High' ? 'danger' : finding.severity === 'Medium' ? 'warn' : 'neutral'}>
                          {formatTime(finding.detectedAt, locale)} · {t(`security.kind.${finding.kind}`)}
                        </Badge>
                      ))}
                    </span>
                  </div>
                </div>
              )}
              {me?.promptTextCollected && (
                <p className="prompt-notice tiny">
                  <Lock size={12} aria-hidden /> {me.developerId === id ? t('prompts.noticeSelf') : t('prompts.noticeManager')}
                </p>
              )}
              {data.sessions.map((session) => (
                <SessionCard key={session.workSessionId} session={session} prompts={promptsBySession.get(session.workSessionId) ?? []} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Total({ label, value, foot }: { label: string; value: ReactNode; foot?: string }) {
  return (
    <div className="day__total">
      <span className="day__total-label">{label}</span>
      <span className="day__total-value">{value}</span>
      {foot && <span className="day__total-label">{foot}</span>}
    </div>
  )
}

const CATEGORY_ICON: Record<WorkCategory, typeof BugIcon> = {
  Bug: BugIcon,
  Spec: FileText,
  Task: Ticket,
  Exploration: Compass,
}

const CATEGORY_TONE: Record<WorkCategory, 'danger' | 'info' | 'accent' | 'neutral'> = {
  Bug: 'danger',
  Spec: 'info',
  Task: 'accent',
  Exploration: 'neutral',
}

/** One session: what it was for, what happened, and where to look further. */
function SessionCard({ session, prompts }: { session: DaySession; prompts: PromptItem[] }) {
  const { t, locale } = useI18n()
  const [open, setOpen] = useState(false)
  const [showPrompts, setShowPrompts] = useState(false)
  const Icon = CATEGORY_ICON[session.category]

  const category = session.category === 'Spec' && session.spec
    ? (session.spec.kind === 'Prd' ? 'PRD' : 'SDD')
    : t(`day.category.${session.category}`)

  return (
    <Panel flush>
      <div className="day__session">
        <div className="row row--tight row--wrap">
          <Icon size={15} className="muted" />
          <Badge tone={CATEGORY_TONE[session.category]}>{category}</Badge>
          <span className="bold">{subjectOf(session, t)}</span>
          {session.spec && session.category !== 'Spec' && (
            <Badge tone="info">{session.spec.kind === 'Prd' ? 'PRD' : 'SDD'} {session.spec.key}</Badge>
          )}
          {session.projectName && <span className="tiny muted">{session.projectName}</span>}
          {session.isSimulated && <Badge tone="demo">{t('day.simulated')}</Badge>}
          <div className="spacer" />
          <Badge tone={session.status === 'Completed' ? 'ok' : session.status === 'Failed' ? 'danger' : 'neutral'}>
            {t(`sessionStatus.${session.status}`)}
          </Badge>
        </div>

        <div className="row row--wrap tiny muted">
          <span className="mono">
            {formatTime(session.startedAt, locale)}–{session.endedAt ? formatTime(session.endedAt, locale) : '…'}
          </span>
          <span>{formatDuration(session.secondsInDay, locale)}</span>
          {session.repositories.map((repo) => <span key={repo} className="chip mono">{repo}</span>)}
          {session.branch && <span className="mono">{session.branch}</span>}
          <span>{session.providerName}{session.modelName ? ` · ${session.modelName}` : ''}</span>
        </div>

        {session.kind === 'TaskWork' && session.steps.length > 0 && (
          <ProgressBar value={session.progressPercent} showValue size="sm" />
        )}

        <div className="day__metrics tiny">
          <span><span style={{ color: 'var(--ok-text)' }}>+{session.linesAdded}</span> <span style={{ color: 'var(--danger-text)' }}>−{session.linesDeleted}</span></span>
          <span>{t('day.filesCount', { count: session.filesChanged })}</span>
          <span><GitCommitHorizontal size={12} /> {session.commits.length}</span>
          {session.testsRun > 0 && <span>{t('day.tests')} {session.testsPassed}/{session.testsRun}</span>}
          <span><MessageSquare size={12} /> {t('day.promptsCount', { count: session.prompts })}</span>
          <span className="mono">{formatCompact(session.inputTokens + session.outputTokens, locale)} {t('activity.tokens')}</span>
        </div>

        {session.summary && (
          <p className="small day__summary">{session.summary}</p>
        )}

        {session.notes.length > 0 && (
          <ul className="day__list small">
            {session.notes.slice(-5).map((note, i) => <li key={i}>{note}</li>)}
          </ul>
        )}

        {session.steps.length > 0 && (
          <div className="row row--wrap tiny">
            <ListChecks size={13} className="muted" />
            {session.steps.map((step, i) => (
              <Badge key={i} tone={step.status === 'Completed' ? 'ok' : step.status === 'InProgress' ? 'accent' : step.status === 'Failed' ? 'danger' : 'neutral'}>
                {step.title}
              </Badge>
            ))}
          </div>
        )}

        {session.commits.length > 0 && (
          <div className="stack tiny" style={{ gap: 2 }}>
            {session.commits.map((commit, i) => (
              <span key={i}>
                <span className="mono muted">{commit.sha}</span> {commit.message}
                {commit.repository && <span className="muted"> · {commit.repository}</span>}
              </span>
            ))}
          </div>
        )}

        {session.blockers.length > 0 && (
          <div className="stack tiny" style={{ gap: 2, color: 'var(--danger-text)' }}>
            {session.blockers.map((blocker, i) => <span key={i}>{blocker}</span>)}
          </div>
        )}

        <div className="row row--wrap">
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
            <ChevronDown size={13} style={{ transform: open ? 'rotate(180deg)' : undefined }} />
            {t('day.timeline')} ({session.events.length})
          </button>
          {prompts.length > 0 && (
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => setShowPrompts((value) => !value)} aria-expanded={showPrompts}>
              <MessageSquare size={13} /> {t('prompts.show', { count: prompts.length })}
            </button>
          )}
          <div className="spacer" />
          {session.category === 'Bug' && (
            <Link className="btn btn--sm btn--ghost" to="/track/bugs">{t('nav.bugHunt')}</Link>
          )}
          {session.spec && (
            <Link className="btn btn--sm btn--ghost" to={`/project/specs?spec=${session.spec.id}`}>{t('day.openSpec')}</Link>
          )}
          <Link className="btn btn--sm" to={`/work/${session.workSessionId}`}>{t('live.viewSession')}</Link>
        </div>

        {showPrompts && (
          <ol className="prompt-list">
            {prompts.map((prompt) => (
              <li key={prompt.id} className="prompt-list__item">
                <div className="row row--tight tiny muted">
                  <span className="mono">{formatTime(prompt.submittedAt, locale)}</span>
                  <span>{t('prompts.chars', { count: prompt.chars })}</span>
                  {prompt.taskExternalId && <Badge tone="accent">{prompt.taskExternalId}</Badge>}
                  {prompt.tickets.filter((ticket) => ticket !== prompt.taskExternalId).map((ticket) => <Badge key={ticket}>{ticket}</Badge>)}
                </div>
                <div className="prompt-list__text">{prompt.text}</div>
              </li>
            ))}
          </ol>
        )}

        {open && (
          <div className="day__events">
            {session.events.map((event, i) => (
              <div key={i} className="row row--tight tiny">
                <span className="mono muted">{formatTime(event.at, locale)}</span>
                <span className="truncate">{event.text}</span>
              </div>
            ))}
            {session.files.length > 0 && (
              <div className="stack tiny" style={{ gap: 2, marginTop: 'var(--s2)' }}>
                <span className="muted">{t('work.filesChanged')}</span>
                {session.files.slice(0, 30).map((file) => <span key={file} className="mono truncate">{file}</span>)}
              </div>
            )}
          </div>
        )}
      </div>
    </Panel>
  )
}
